import { emptyValueForSchema, getSchemaAt, CASE_TYPE_SCHEMA } from "../../../../case-spec";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrayShell,
  BoolField,
  EnumSelect,
  MediaUrlField,
  MediaUrlListField,
  RowCard,
  TextField,
  useOpenIndex,
} from "../shared-ui";
import {
  asArray,
  bool,
  enumOptionsAt,
  isRecord,
  num,
  str,
  type PanelProps,
} from "../shared";
import {
  appendAtPath,
  childPath,
  deleteAtPath,
  getAtPath,
  removeAtIndex,
  setAtPath,
} from "../path-utils";
import { setStringField } from "../optional-field";

type FieldSpec = {
  key: string;
  label: string;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  kind?:
    | "text"
    | "bool"
    | "number"
    | "media_url"
    | "audio_url"
    | "video_url"
    | "enum";
  options?: string[];
  optionLabels?: Record<string, string>;
  optional?: boolean;
  visibleWhen?: (record: Record<string, unknown>) => boolean;
  clearKeysByValue?: Record<string, string[]>;
};

export function TextRecordsPanel({
  root,
  disabled,
  onChangeRoot,
  path,
  title,
  fields,
  rowTitleKeys,
  renderAfterFields,
}: PanelProps & {
  path: string;
  title: string;
  fields: FieldSpec[];
  rowTitleKeys: string[];
  renderAfterFields?: (input: {
    root: unknown;
    record: Record<string, unknown>;
    itemPath: string;
    disabled?: boolean;
    onChangeRoot: (next: unknown) => void;
  }) => ReactNode;
}) {
  const items = asArray(getAtPath(root, path));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(items.length);
  const itemSchema = getSchemaAt(CASE_TYPE_SCHEMA, `${path}[0]`);

  return (
    <ArrayShell
      title={title}
      count={items.length}
      disabled={disabled}
      onAdd={() => {
        const row = itemSchema
          ? emptyValueForSchema(itemSchema)
          : Object.fromEntries(fields.map((f) => [f.key, f.kind === "bool" ? false : ""]));
        onChangeRoot(appendAtPath(root, path, row));
        setOpenIndex(items.length);
      }}
    >
      <ul className="flex flex-col gap-2">
        {items.map((item, index) => {
          const rec = isRecord(item) ? item : {};
          const itemPath = childPath(path, index);
          const titleParts = rowTitleKeys
            .map((k) => str(rec[k]))
            .filter(Boolean);
          const rowTitle =
            titleParts.join(" · ") || `${title} ${index + 1}`;
          return (
            <RowCard
              key={itemPath}
              title={
                rowTitle.length > 64 ? `${rowTitle.slice(0, 64)}…` : rowTitle
              }
              open={openIndex === index}
              onToggle={() => toggle(index)}
              disabled={disabled}
              index={index}
              total={items.length}
              arrayPath={path}
              root={root}
              onChangeRoot={onChangeRoot}
            >
              {fields.map((f) => {
                if (f.visibleWhen && !f.visibleWhen(rec)) return null;
                if (f.kind === "bool") {
                  return (
                    <BoolField
                      key={f.key}
                      fieldKey={f.key}
                      label={f.label}
                      value={bool(rec[f.key])}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, f.key), v),
                        )
                      }
                    />
                  );
                }
                if (f.kind === "number") {
                  const n =
                    typeof rec[f.key] === "number" &&
                    Number.isFinite(rec[f.key])
                      ? (rec[f.key] as number)
                      : f.optional
                        ? ""
                        : 0;
                  return (
                    <TextField
                      key={f.key}
                      fieldKey={f.key}
                      label={f.label}
                      value={String(n)}
                      disabled={disabled}
                      onChange={(v) => {
                        const fieldPath = childPath(itemPath, f.key);
                        onChangeRoot(
                          f.optional && v === ""
                            ? deleteAtPath(root, fieldPath)
                            : setAtPath(root, fieldPath, Number(v) || 0),
                        );
                      }}
                    />
                  );
                }
                if (f.kind === "enum" && f.options) {
                  return (
                    <EnumSelect
                      key={f.key}
                      label={f.label}
                      fieldKey={f.key}
                      value={str(rec[f.key])}
                      options={f.options}
                      optionLabels={f.optionLabels}
                      disabled={disabled}
                      onChange={(v) => {
                        let next = setAtPath(root, childPath(itemPath, f.key), v);
                        for (const key of f.clearKeysByValue?.[v] ?? []) {
                          next = deleteAtPath(next, childPath(itemPath, key));
                        }
                        onChangeRoot(next);
                      }}
                    />
                  );
                }
                if (
                  f.kind === "media_url" ||
                  f.kind === "audio_url" ||
                  f.kind === "video_url"
                ) {
                  return (
                    <MediaUrlField
                      key={f.key}
                      label={f.label}
                      value={str(rec[f.key])}
                      disabled={disabled}
                      mediaKind={
                        f.kind === "audio_url"
                          ? "audio"
                          : f.kind === "video_url"
                            ? "video"
                            : "image"
                      }
                      fieldKey={f.key}
                      placeholder={f.placeholder}
                      onChange={(v) => {
                        const fieldPath = childPath(itemPath, f.key);
                        onChangeRoot(
                          setStringField(root, fieldPath, v, Boolean(f.optional)),
                        );
                      }}
                    />
                  );
                }
                return (
                  <TextField
                    key={f.key}
                    fieldKey={f.key}
                    label={f.label}
                    value={str(rec[f.key])}
                    disabled={disabled}
                    multiline={f.multiline}
                    rows={f.rows}
                    placeholder={f.placeholder}
                    onChange={(v) => {
                      const fieldPath = childPath(itemPath, f.key);
                      onChangeRoot(
                        setStringField(root, fieldPath, v, Boolean(f.optional)),
                      );
                    }}
                  />
                );
              })}
              {renderAfterFields?.({
                root,
                record: rec,
                itemPath,
                disabled,
                onChangeRoot,
              })}
            </RowCard>
          );
        })}
      </ul>
    </ArrayShell>
  );
}

export function NotesPanel(props: PanelProps) {
  return (
    <TextRecordsPanel
      {...props}
      path="device.apps.notes"
      title="备忘录"
      rowTitleKeys={["title"]}
      fields={[
        { key: "title", label: "标题" },
        { key: "created_at", label: "创建时间", placeholder: "YYYY-MM-DDTHH:mm:ss" },
        { key: "body", label: "正文", multiline: true, rows: 8 },
        {
          key: "password",
          label: "应用密码",
          placeholder: "填写后手机端将要求输入此密码解锁",
          optional: true,
        },
      ]}
    />
  );
}

function GmailAttachmentsEditor({
  root,
  record,
  itemPath,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  record: Record<string, unknown>;
  itemPath: string;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const path = childPath(itemPath, "attachments");
  const attachments = asArray(record.attachments);
  const attachmentSchema = getSchemaAt(
    CASE_TYPE_SCHEMA,
    "device.apps.gmail[0].attachments[0]",
  );

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border/60 p-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium">PDF 附件 ({attachments.length})</p>
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={disabled}
          onClick={() => {
            const attachment = attachmentSchema
              ? emptyValueForSchema(attachmentSchema)
              : {
                  filename: "",
                  mime_type: "application/pdf",
                  url: "",
                };
            onChangeRoot(appendAtPath(root, path, attachment));
          }}
        >
          添加附件
        </Button>
      </div>
      {attachments.length === 0 ? (
        <p className="text-xs text-muted-foreground">暂无附件</p>
      ) : (
        attachments.map((attachment, index) => {
          const rec = isRecord(attachment) ? attachment : {};
          const attachmentPath = childPath(path, index);
          const bytes = num(rec.size_bytes);
          return (
            <div
              key={attachmentPath}
              className="flex flex-col gap-2 rounded-md border border-border/60 p-2"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-xs text-muted-foreground">
                  {str(rec.filename) || `附件 ${index + 1}`}
                </p>
                <Button
                  type="button"
                  size="icon-xs"
                  variant="ghost"
                  disabled={disabled}
                  title="删除附件"
                  className="shrink-0 text-destructive hover:text-destructive"
                  onClick={() =>
                    onChangeRoot(removeAtIndex(root, path, index))
                  }
                >
                  ✕
                </Button>
              </div>
              <MediaUrlField
                label="PDF 文件"
                value={str(rec.url)}
                disabled={disabled}
                mediaKind="pdf"
                fieldKey="gmail_attachment_url"
                placeholder="上传 PDF 或填写 HTTPS 地址"
                onChange={(value) =>
                  onChangeRoot(
                    setAtPath(root, childPath(attachmentPath, "url"), value),
                  )
                }
                onUpload={(uploaded) => {
                  let next = setAtPath(
                    root,
                    childPath(attachmentPath, "url"),
                    uploaded.url,
                  );
                  next = setAtPath(
                    next,
                    childPath(attachmentPath, "filename"),
                    uploaded.name,
                  );
                  next = setAtPath(
                    next,
                    childPath(attachmentPath, "mime_type"),
                    "application/pdf",
                  );
                  next = setAtPath(
                    next,
                    childPath(attachmentPath, "size_bytes"),
                    uploaded.size,
                  );
                  onChangeRoot(next);
                }}
              />
              <TextField
                label="文件名"
                fieldKey="filename"
                value={str(rec.filename)}
                disabled={disabled}
                placeholder="Seller_Guide.pdf"
                onChange={(value) =>
                  onChangeRoot(
                    setAtPath(
                      root,
                      childPath(attachmentPath, "filename"),
                      value,
                    ),
                  )
                }
              />
              <p className="text-xs text-muted-foreground">
                体积：{bytes > 0 ? `${Math.round(bytes / 1024)} KB` : "上传后自动写入"}
              </p>
            </div>
          );
        })
      )}
    </div>
  );
}

export function GmailPanel(props: PanelProps) {
  return (
    <TextRecordsPanel
      {...props}
      path="device.apps.gmail"
      title="邮件"
      rowTitleKeys={["subject", "sender_email"]}
      fields={[
        { key: "subject", label: "主题" },
        {
          key: "direction",
          label: "方向",
          kind: "enum",
          options: enumOptionsAt("device.apps.gmail[0].direction"),
          optionLabels: {
            inbox: "收件箱",
            sent: "已发送",
            draft: "草稿",
          },
        },
        { key: "sender_email", label: "发件邮箱" },
        { key: "recipient_email", label: "收件邮箱" },
        {
          key: "received_at",
          label: "收到时间",
          placeholder: "YYYY-MM-DDTHH:mm:ss",
        },
        { key: "body", label: "正文", multiline: true, rows: 8 },
      ]}
      renderAfterFields={(input) => <GmailAttachmentsEditor {...input} />}
    />
  );
}

export function SafariPanel(props: PanelProps) {
  const { root, disabled, onChangeRoot } = props;
  const path = "device.apps.safari";
  const items = asArray(getAtPath(root, path));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(items.length);
  const itemSchema = getSchemaAt(CASE_TYPE_SCHEMA, "device.apps.safari[0]");

  return (
    <ArrayShell
      title="Safari"
      count={items.length}
      disabled={disabled}
      onAdd={() => {
        const row = itemSchema
          ? emptyValueForSchema(itemSchema)
          : {
              visited_at: "",
              url: "",
              title: "",
              is_bookmark: false,
            };
        onChangeRoot(appendAtPath(root, path, row));
        setOpenIndex(items.length);
      }}
    >
      <ul className="flex flex-col gap-2">
        {items.map((item, index) => {
          const rec = isRecord(item) ? item : {};
          const itemPath = childPath(path, index);
          const title = str(rec.title) || str(rec.url) || `记录 ${index + 1}`;
          const page = isRecord(rec.page) ? rec.page : null;
          return (
            <RowCard
              key={itemPath}
              title={title}
              open={openIndex === index}
              onToggle={() => toggle(index)}
              disabled={disabled}
              index={index}
              total={items.length}
              arrayPath={path}
              root={root}
              onChangeRoot={onChangeRoot}
            >
              <TextField
                label="标题 / 搜索词"
                value={str(rec.title)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "title"), v),
                  )
                }
              />
              <TextField
                label="网址"
                value={str(rec.url)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(setAtPath(root, childPath(itemPath, "url"), v))
                }
              />
              <TextField
                label="访问时间"
                fieldKey="visited_at"
                value={str(rec.visited_at)}
                disabled={disabled}
                placeholder="YYYY-MM-DDTHH:mm:ss"
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "visited_at"), v),
                  )
                }
              />
              <BoolField
                label="书签"
                value={bool(rec.is_bookmark)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "is_bookmark"), v),
                  )
                }
              />
              <SafariSearchResultsEditor
                root={root}
                path={childPath(itemPath, "search_results")}
                disabled={disabled}
                onChangeRoot={onChangeRoot}
              />
              <p className="text-xs font-medium text-muted-foreground">
                必须正好 10 条，且每条都要填写可打开的页面内容。搜索记录还要填写 3 条搜索结果，第一条对应下方页面。
              </p>
              <TextField
                label="站点名"
                value={str(page?.site_name)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "page.site_name"), v),
                  )
                }
              />
              <TextField
                label="栏目"
                value={str(page?.section)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "page.section"), v),
                  )
                }
              />
              <TextField
                label="页面标题"
                value={str(page?.headline)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "page.headline"), v),
                  )
                }
              />
              <TextField
                label="导语"
                value={str(page?.standfirst)}
                disabled={disabled}
                multiline
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "page.standfirst"), v),
                  )
                }
              />
              <TextField
                label="作者 / 来源"
                value={str(page?.byline)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "page.byline"), v),
                  )
                }
              />
              <TextField
                label="发布日期"
                value={str(page?.published_at)}
                disabled={disabled}
                placeholder="YYYY-MM-DDTHH:mm:ss"
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(
                      root,
                      childPath(itemPath, "page.published_at"),
                      v,
                    ),
                  )
                }
              />
              <TextField
                label="来源链接"
                value={str(page?.source_url)}
                disabled={disabled}
                placeholder="https://…"
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "page.source_url"), v),
                  )
                }
              />
              <TextField
                label="阅读分钟"
                value={
                  page?.reading_time_min == null
                    ? ""
                    : String(num(page.reading_time_min))
                }
                disabled={disabled}
                onChange={(v) => {
                  const path = childPath(itemPath, "page.reading_time_min");
                  onChangeRoot(
                    v === ""
                      ? deleteAtPath(root, path)
                      : setAtPath(root, path, Number(v)),
                  );
                }}
              />
              <MediaUrlListField
                root={root}
                path={childPath(itemPath, "page.hero_image_urls")}
                label="头图"
                disabled={disabled}
                fieldKey="hero_image_urls"
                onChangeRoot={onChangeRoot}
              />
              <SafariBlocksEditor
                root={root}
                path={childPath(itemPath, "page.blocks")}
                disabled={disabled}
                onChangeRoot={onChangeRoot}
              />
              <TextField
                label="页脚"
                value={str(page?.footer_note)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "page.footer_note"), v),
                  )
                }
              />
            </RowCard>
          );
        })}
      </ul>
    </ArrayShell>
  );
}

export function SafariBookmarksPanel(props: PanelProps) {
  const { root, disabled, onChangeRoot } = props;
  const path = "device.apps.safari_bookmarks";
  const data = getAtPath(root, path);
  const rec = isRecord(data) ? data : {};
  let editableRoot = root;
  if (typeof rec.favorites_count !== "number") {
    editableRoot = setAtPath(editableRoot, childPath(path, "favorites_count"), 0);
  }
  if (typeof rec.tab_group_favorites_count !== "number") {
    editableRoot = setAtPath(
      editableRoot,
      childPath(path, "tab_group_favorites_count"),
      0,
    );
  }

  const updateCount = (field: string, value: string) => {
    const parsed = Number(value);
    onChangeRoot(
      setAtPath(
        editableRoot,
        childPath(path, field),
        Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : 0,
      ),
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-md border border-border/60 p-3">
        <div>
          <p className="text-sm font-medium">书签文件夹</p>
          <p className="text-xs text-muted-foreground">
            这两个数字显示在手机端文件夹右侧，不影响下方书签列表。
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label="常用项目数量"
            fieldKey="favorites_count"
            value={String(num(rec.favorites_count))}
            disabled={disabled}
            onChange={(value) => updateCount("favorites_count", value)}
          />
          <TextField
            label="标签页组个人收藏数量"
            fieldKey="tab_group_favorites_count"
            value={String(num(rec.tab_group_favorites_count))}
            disabled={disabled}
            onChange={(value) =>
              updateCount("tab_group_favorites_count", value)
            }
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        生成和发布内容至少需要 10 条。手机端仅展示，不打开网址。
      </p>
      <TextRecordsPanel
        {...props}
        root={editableRoot}
        path={`${path}.items`}
        title="Safari 书签"
        rowTitleKeys={["title", "url"]}
        fields={[
          { key: "title", label: "标题" },
          { key: "url", label: "网址", placeholder: "https://…" },
          {
            key: "context_path",
            label: "主角关联依据",
            placeholder: "例如 device.apps.calendar[0]",
            optional: true,
          },
          {
            key: "context_anchor",
            label: "关联关键词",
            placeholder: "须同时出现在关联内容和书签标题中",
            optional: true,
          },
          {
            key: "favicon_url",
            label: "站点图标",
            kind: "media_url",
            optional: true,
          },
        ]}
      />
    </div>
  );
}

function emptySearchResult() {
  return { title: "", display_url: "", snippet: "" };
}

function SafariSearchResultsEditor({
  root,
  path,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  path: string;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const items = asArray(getAtPath(root, path));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium">搜索结果 ({items.length})</p>
        {items.length === 0 ? (
          <button
            type="button"
            className="text-xs text-primary underline-offset-2 hover:underline disabled:opacity-50"
            disabled={disabled}
            onClick={() =>
              onChangeRoot(
                setAtPath(root, path, [
                  emptySearchResult(),
                  emptySearchResult(),
                  emptySearchResult(),
                ]),
              )
            }
          >
            添加 3 条结果
          </button>
        ) : (
          <button
            type="button"
            className="text-xs text-primary underline-offset-2 hover:underline disabled:opacity-50"
            disabled={disabled}
            onClick={() => onChangeRoot(deleteAtPath(root, path))}
          >
            清除
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        搜索记录填这里，必须正好 3 条。第 1 条打开下方页面。普通网址访问不要填。
      </p>
      {items.map((item, index) => {
        const rec = isRecord(item) ? item : {};
        const itemPath = childPath(path, index);
        return (
          <div
            key={index}
            className="flex flex-col gap-2 rounded-md border border-border/60 p-2"
          >
            <p className="text-xs text-muted-foreground">结果 {index + 1}</p>
            <TextField
              label="标题"
              value={str(rec.title)}
              disabled={disabled}
              onChange={(v) =>
                onChangeRoot(setAtPath(root, childPath(itemPath, "title"), v))
              }
            />
            <TextField
              label="显示网址"
              value={str(rec.display_url)}
              disabled={disabled}
              placeholder="example.com › path"
              onChange={(v) =>
                onChangeRoot(
                  setAtPath(root, childPath(itemPath, "display_url"), v),
                )
              }
            />
            <TextField
              label="摘要"
              value={str(rec.snippet)}
              disabled={disabled}
              multiline
              onChange={(v) =>
                onChangeRoot(setAtPath(root, childPath(itemPath, "snippet"), v))
              }
            />
            <MediaUrlField
              label="站点图标"
              value={str(rec.favicon_url)}
              disabled={disabled}
              fieldKey="favicon_url"
              onChange={(v) =>
                onChangeRoot(
                  setAtPath(root, childPath(itemPath, "favicon_url"), v),
                )
              }
            />
          </div>
        );
      })}
    </div>
  );
}

function SafariBlocksEditor({
  root,
  path,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  path: string;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const blocks = asArray(getAtPath(root, path));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium">正文块 ({blocks.length})</p>
        <button
          type="button"
          className="text-xs text-primary underline-offset-2 hover:underline disabled:opacity-50"
          disabled={disabled}
          onClick={() =>
            onChangeRoot(
              appendAtPath(root, path, { kind: "paragraph", text: "" }),
            )
          }
        >
          添加段落
        </button>
      </div>
      {blocks.map((block, i) => {
        const rec = isRecord(block) ? block : {};
        const bPath = childPath(path, i);
        const kind = str(rec.kind, "paragraph");
        return (
          <div
            key={i}
            className="flex flex-col gap-2 rounded-md border border-border/60 p-2"
          >
            <TextField
              label="类型"
              value={kind}
              disabled={disabled}
              placeholder="paragraph | heading | list | quote | callout"
              onChange={(v) =>
                onChangeRoot(setAtPath(root, childPath(bPath, "kind"), v))
              }
            />
            {kind === "list" ? (
              <TextField
                label="列表项（每行一项）"
                value={asArray(rec.items)
                  .map((x) => str(x))
                  .join("\n")}
                disabled={disabled}
                multiline
                rows={4}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(bPath, "items"), v.split("\n")),
                  )
                }
              />
            ) : (
              <TextField
                label="文本"
                value={str(rec.text)}
                disabled={disabled}
                multiline
                rows={3}
                onChange={(v) =>
                  onChangeRoot(setAtPath(root, childPath(bPath, "text"), v))
                }
              />
            )}
            {kind === "quote" ? (
              <TextField
                label="出处"
                value={str(rec.attribution)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(bPath, "attribution"), v),
                  )
                }
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function CalendarPanel(props: PanelProps) {
  return (
    <TextRecordsPanel
      {...props}
      path="device.apps.calendar"
      title="日历"
      rowTitleKeys={["title", "start_at"]}
      fields={[
        { key: "title", label: "标题" },
        { key: "start_at", label: "开始", placeholder: "YYYY-MM-DDTHH:mm:ss" },
        { key: "end_at", label: "结束", placeholder: "YYYY-MM-DDTHH:mm:ss" },
        { key: "location", label: "地点" },
        { key: "calendar", label: "日历名" },
        { key: "note", label: "备注", multiline: true, rows: 4, optional: true },
      ]}
    />
  );
}



export function SocialFeedPanel({
  path,
  title,
  ...props
}: PanelProps & { path: string; title: string }) {
  return (
    <TextRecordsPanel
      {...props}
      path={path}
      title={title}
      rowTitleKeys={["author_name", "caption"]}
      fields={[
        { key: "platform", label: "平台" },
        { key: "author_handle", label: "账号" },
        { key: "author_name", label: "作者名" },
        { key: "caption", label: "文案", multiline: true, rows: 4 },
        { key: "location_name", label: "地点" },
        {
          key: "posted_at",
          label: "发布时间",
          placeholder: "YYYY-MM-DDTHH:mm:ss",
        },
        { key: "likes_count", label: "点赞数", kind: "number" },
        { key: "image_url", label: "图片 URL", kind: "media_url" },
      ]}
    />
  );
}

export function VoiceMemosPanel(props: PanelProps) {
  return (
    <TextRecordsPanel
      {...props}
      path="device.apps.voice-memos"
      title="语音备忘录"
      rowTitleKeys={["location", "recorded_at"]}
      fields={[
        { key: "location", label: "地点" },
        {
          key: "recorded_at",
          label: "录制时间",
          placeholder: "YYYY-MM-DDTHH:mm:ss",
        },
        { key: "audio_url", label: "音频", kind: "audio_url" },
      ]}
    />
  );
}

function HealthSamplesEditor({
  root,
  record,
  itemPath,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  record: Record<string, unknown>;
  itemPath: string;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const path = childPath(itemPath, "heart_rate_samples");
  const samples = asArray(record.heart_rate_samples);
  const sampleSchema = getSchemaAt(
    CASE_TYPE_SCHEMA,
    "device.apps.health[0].heart_rate_samples[0]",
  );

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border/60 p-2">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-xs font-medium">分钟级心率 ({samples.length})</p>
          <p className="text-[0.65rem] text-muted-foreground">
            按时间顺序填写，午夜可使用次日 00:00。
          </p>
        </div>
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={disabled}
          onClick={() => {
            const sample = sampleSchema
              ? emptyValueForSchema(sampleSchema)
              : { recorded_at: "", bpm: 0 };
            onChangeRoot(appendAtPath(root, path, sample));
          }}
        >
          添加样本
        </Button>
      </div>
      {samples.length === 0 ? (
        <p className="text-xs text-muted-foreground">暂无分钟级心率。</p>
      ) : (
        samples.map((sample, index) => {
          const rec = isRecord(sample) ? sample : {};
          const samplePath = childPath(path, index);
          const bpm =
            typeof rec.bpm === "number" && Number.isFinite(rec.bpm)
              ? String(rec.bpm)
              : "";
          return (
            <div
              key={samplePath}
              className="grid grid-cols-[minmax(0,1fr)_7rem_auto] items-end gap-2 rounded-md border border-border/60 p-2"
            >
              <TextField
                label="测量时间"
                fieldKey="recorded_at"
                value={str(rec.recorded_at)}
                disabled={disabled}
                placeholder="YYYY-MM-DDTHH:mm:ss"
                onChange={(value) =>
                  onChangeRoot(
                    setAtPath(
                      root,
                      childPath(samplePath, "recorded_at"),
                      value,
                    ),
                  )
                }
              />
              <TextField
                label="心率 BPM"
                fieldKey="bpm"
                value={bpm}
                disabled={disabled}
                onChange={(value) =>
                  onChangeRoot(
                    setAtPath(
                      root,
                      childPath(samplePath, "bpm"),
                      Number(value) || 0,
                    ),
                  )
                }
              />
              <Button
                type="button"
                size="icon-xs"
                variant="ghost"
                disabled={disabled}
                title="删除样本"
                className="text-destructive hover:text-destructive"
                onClick={() =>
                  onChangeRoot(removeAtIndex(root, path, index))
                }
              >
                ✕
              </Button>
            </div>
          );
        })
      )}
    </div>
  );
}

export function HealthPanel(props: PanelProps) {
  return (
    <TextRecordsPanel
      {...props}
      path="device.apps.health"
      title="健康"
      rowTitleKeys={["day"]}
      fields={[
        { key: "day", label: "日期", placeholder: "YYYY-MM-DD" },
        { key: "step_count", label: "步数", kind: "number" },
        { key: "sleep_hours", label: "睡眠小时", kind: "number" },
        {
          key: "heart_rate_avg",
          label: "平均心率",
          kind: "number",
          optional: true,
        },
        {
          key: "heart_rate_summary_title",
          label: "摘要类型",
          placeholder: "例如：睡眠、夜间活动",
          optional: true,
        },
        {
          key: "heart_rate_summary",
          label: "摘要正文",
          multiline: true,
          rows: 4,
          optional: true,
        },
      ]}
      renderAfterFields={(input) => <HealthSamplesEditor {...input} />}
    />
  );
}
